import moment from 'moment';
import * as yup from 'yup';

declare module 'yup' {
  interface StringSchema {
    rfc(message?: string): StringSchema;
    numeric(length?: number, message?: string): StringSchema;
    passwordValidation(message?: string): StringSchema;
    postalCode(message?: string): StringSchema;
    decimalPlaces(maxDecimals?: number, message?: string): StringSchema;
    datetimeFormat(message?: string): StringSchema;
    creationDate(messages?: CreationDateValidationMessages): StringSchema;
  }

  interface NumberSchema {
    decimalPlaces(maxDecimals?: number, message?: string): NumberSchema;
  }
}

export interface CreationDateValidationMessages {
  formatMessage?: string;
  invalidDateMessage?: string;
  futureDateMessage?: string;
  pastDateMessage?: string;
}

/**
 * @author: Omar Sánchez Martinez
 * @since: 02/06/2025
 * @description: Valida el RFC
 */
yup.addMethod<yup.StringSchema>(yup.string, 'rfc', function (message = 'Ingreses un RFC válido') {
  return this.test('rfc', message, function (value: string | undefined) {
    const { path, createError } = this;

    if (!value) return true;

    const rfcRegex =
      /^[A-ZÑ&]{3,4}([0-9]{2})(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])[A-Z0-9]{2}[0-9A]$/u;

    return rfcRegex.test(value) || createError({ path, message });
  });
});

/**
 * @author: Omar Sánchez Martinez
 * @since: 02/06/2025
 * @description: Valida que solo sean números
 */
yup.addMethod<yup.StringSchema>(
  yup.string,
  'numeric',
  function (length?: number, message?: string) {
    return this.test(
      'numeric',
      message ||
        (length
          ? '${path} debe contener exactamente ' + length + ' números'
          : '${path} debe contener solo números'),
      function (value: string | undefined) {
        const { path, createError } = this;
        if (!value) return true; // let required() handle empty
        const numericRegex = /^[0-9]+$/;

        if (!numericRegex.test(value)) {
          return createError({ path, message: '${path} debe contener solo números' }); //TODO: Cambiar a message
        }

        if (length && value.length !== length) {
          return createError({
            path,
            message: '${path} debe contener exactamente ' + length + ' números',
          });
        }

        return true;
      },
    );
  },
);

const defaultCreationDateMessages: Required<CreationDateValidationMessages> = {
  formatMessage: 'La Fecha debe tener el formato YYYY-MM-DD',
  invalidDateMessage: 'La Fecha debe ser una fecha válida',
  futureDateMessage: 'La Fecha no puede ser mayor a la fecha actual',
  pastDateMessage: 'La Fecha no puede ser anterior a 3 días',
};

yup.addMethod<yup.StringSchema>(
  yup.string,
  'creationDate',
  function (messages: CreationDateValidationMessages = {}) {
    const { formatMessage, invalidDateMessage, futureDateMessage, pastDateMessage } = {
      ...defaultCreationDateMessages,
      ...messages,
    };

    return this.test('creation-date-format', formatMessage, function (value: string | undefined) {
      if (!value) return true;
      return /^\d{4}-\d{2}-\d{2}$/.test(value);
    })
      .test('creation-date-valid', invalidDateMessage, function (value: string | undefined) {
        if (!value) return true;

        return moment(value, 'YYYY-MM-DD', true).isValid();
      })
      .test('creation-date-not-future', futureDateMessage, function (value: string | undefined) {
        if (!value) return true;

        const parsed = moment(value, 'YYYY-MM-DD', true);
        if (!parsed.isValid()) return true;

        return parsed.isSameOrBefore(moment(), 'day');
      })
      .test('creation-date-not-too-old', pastDateMessage, function (value: string | undefined) {
        if (!value) return true;

        const parsed = moment(value, 'YYYY-MM-DD', true);
        if (!parsed.isValid()) return true;

        return parsed.isSameOrAfter(moment().subtract(3, 'days'), 'day');
      });
  },
);

/**
 * @author: Omar Sánchez Martinez
 * @since: 02/06/2025
 * @description: Valida la contraseña
 */
yup.addMethod<yup.StringSchema>(
  yup.string,
  'passwordValidation',
  function (
    message = 'Para mayor seguridad la contraseña debe tener al menos 1 mayúscula, 1 minúscula y 1 número',
  ) {
    return this.test('passwordValidation', message, function (value: string | undefined) {
      const { path, createError } = this;
      if (!value) return true;
      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[?\da-zA-Z]).{3,}$/;

      return passwordRegex.test(value) || createError({ path, message });
    });
  },
);

/**
 * @author: Omar Sánchez Martinez
 * @since: 02/06/2025
 * @description: Valida que solo sean números
 */
yup.addMethod<yup.StringSchema>(
  yup.string,
  'postalCode',
  function (message = 'El código postal debe tener 5 dígitos') {
    return this.test('postalCode', message, function (value: string | undefined) {
      const { path, createError } = this;
      if (!value) return true;
      const postalCodeRegex = /^[0-9]{5}$/;
      return postalCodeRegex.test(value) || createError({ path, message });
    });
  },
);

/**
 * @author: Omar Sánchez Martínez
 * @since: 02/06/2025
 * @description: Función helper para validar decimales
 */
const validateDecimalPlaces = (
  value: string | number | undefined,
  maxDecimals: number,
  message?: string,
  path?: string,
  createError?: (params: { path: string; message: string }) => yup.ValidationError,
) => {
  if (!value) return true; // let required() handle empty

  // Convert to string and check if it's a valid number
  const stringValue = value.toString();

  // Check if it's a valid number (including decimals)
  // Allow trailing decimal point (e.g., "22.") for better UX during input
  const numberRegex = /^-?\d+\.?\d*$/;
  if (!numberRegex.test(stringValue)) {
    return path && createError
      ? createError({ path, message: 'El valor debe ser un número válido' })
      : false;
  }

  // Check decimal places
  const decimalPlaces = stringValue.split('.')[1]?.length || 0;

  if (decimalPlaces > maxDecimals) {
    return path && createError
      ? createError({
          path,
          message:
            message ||
            `El valor puede tener máximo ${maxDecimals} decimal${maxDecimals !== 1 ? 'es' : ''}`,
        })
      : false;
  }

  return true;
};

/**
 * @author: Omar Sánchez Martínez
 * @since: 02/06/2025
 * @description: Valida que un número tenga máximo un número específico de decimales (por defecto 2)
 */
yup.addMethod<yup.StringSchema>(
  yup.string,
  'decimalPlaces',
  function (maxDecimals = 2, message?: string) {
    return this.test(
      'decimalPlaces',
      message ||
        `El valor puede tener máximo ${maxDecimals} decimal${maxDecimals !== 1 ? 'es' : ''}`,
      function (value: string | undefined) {
        const { path, createError } = this;
        return validateDecimalPlaces(value, maxDecimals, message, path, createError);
      },
    );
  },
);

/**
 * @author: Omar Sánchez Martínez
 * @since: 02/06/2025
 * @description: Valida que un número tenga máximo un número específico de decimales (por defecto 2)
 */
yup.addMethod<yup.NumberSchema>(
  yup.number,
  'decimalPlaces',
  function (maxDecimals = 2, message?: string) {
    return this.test(
      'decimalPlaces',
      message ||
        `El valor puede tener máximo ${maxDecimals} decimal${maxDecimals !== 1 ? 'es' : ''}`,
      function (value: number | undefined) {
        const { path, createError } = this;
        return validateDecimalPlaces(value, maxDecimals, message, path, createError);
      },
    );
  },
);

/**
 * @author: Omar Sánchez Martínez
 * @since 23/09/2025
 * @description Valida cadenas de fecha y hora con formato YYYY-MM-DD HH:mm:ss
 */
yup.addMethod<yup.StringSchema>(
  yup.string,
  'datetimeFormat',
  function (message = 'La fecha y hora deben tener el formato YYYY-MM-DD HH:mm:ss') {
    return this.test('datetimeFormat', message, function (value: string | undefined) {
      const { path, createError } = this;
      if (!value) return true;

      const datetimeRegex = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/;

      return datetimeRegex.test(value) ? true : createError({ path, message });
    });
  },
);
